<template>
  <CreateBase
    v-if="tenderTemplate"
    :current-step="1"
    :title="$t('Tender estimate')"
    v-on="$listeners"
  >
    <TabsSelector class="mx-8">
      <Tab
        v-for="(tab, index) in tableTabs"
        :key="tab.id"
        :title="tab.title"
        :index="index - 1"
        :badge="positionsCount(tab.id)"
        :selected="currentId === tab.id"
        blue
        @click.native="selectTab(tab.id)"
      />
    </TabsSelector>

    <Summary
      v-show="currentId === 0"
      :items="tenderTemplate"
      :selected-counts="selectedCounts"
      :selected-blocks="selectedSummary"
      :disabled-rows="disabledRows"
      :main-rows="mainRows"
      @select="updateRows"
    />
    <div v-show="currentId > 0">
      <CreateTenderTable
        v-for="table in tenderTemplate"
        v-show="currentId === table.id"
        :key="table.id"
        v-model="selectedCounts[table.id]"
        :order="table.order"
        :rows="table.rows"
        :disabled-rows="disabledRows"
        @select="(rowIds, isAll) => handleSelection(table.id, rowIds, isAll)"
      />
    </div>
    <template #buttons>
      <div class="spacex-12">
        <Button color="neutral" @click="closeModal">
          {{ $t('Cancel') }}
        </Button>
        <Button action-type="button" @click="nextStep">
          {{ $t('Next step') }}
        </Button>
      </div>
    </template>
  </CreateBase>
</template>

<script>
  import CreateBase from '@/containers/tenders/CreateBase';
  import Button from '@/components/ui/Button';
  import projectTender from '@/gql/projects/tender-template.graphql';
  import Summary from '@/containers/tenders/create/Summary';
  import { fieldsOrder } from '@/utils/fields/order-rows';
  import CreateTenderTable from '@/containers/tenders/create/CreateTenderTable';
  import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';
  import mergeArrays from '@/utils/formatting/merge-arrays';
  import request from '@/utils/api/request';
  import { add, areSetsEqual } from '@/utils/arrays';
  import TabsSelector from '@/components/tabs/TabsSelector';
  import Tab from '@/components/tabs/Tab';

  export default {
    name: 'PickPositions',
    components: {
      Tab,
      TabsSelector,
      CreateBase,
      Button,
      Summary,
      CreateTenderTable,
    },
    data() {
      return {
        currentId: 0,
        order: [],
        originalSelectedCounts: {}, // выбранные изначально строчки для отслеживания изменений
        selectedCounts: {},
        selectedSummary: [],
        disabledRows: [],
        mainRows: [],
      };
    },
    async created() {
      if (this.$route.query.tab) {
        this.currentId = this.$route.query.tab;
        let query = Object.assign({}, this.$route.query);
        delete query.tab;
        await this.$router.replace({ query });
      }
    },
    apollo: {
      tenderTemplate() {
        return {
          fetchPolicy: 'no-cache',
          query: projectTender,
          variables: {
            id: this.$route.params.id,
          },
          update(data) {
            let selectedRows = [];
            this.mainRows = [];
            if (data) {
              let list = data.lists || [];

              this.order = fieldsOrder(list.fields_order || {}, list.columns_width || {});

              list.forEach((table) => {
                let rows = [];
                table.rows.forEach((row) => {
                  if (row.group_level > 0) this.mainRows.push(row.id);
                  if (this.tender.row_ids.includes(row.id)) rows.push(row.id);
                });
                this.selectedCounts[table.id] = rows;
                selectedRows.push(...rows);

                if (rows.length) this.selectedSummary.push(table.id);
              });
              this.originalSelectedCounts = { ...this.selectedCounts };
              this.updateDisabledRows(selectedRows);

              return list;
            }
            return [];
          },
        };
      },
    },
    computed: {
      ...mapState('tenderEdit', ['tender']),
      ...mapGetters('tenderEdit', ['editId']),
      tableTabs() {
        return [
          {
            order: 0,
            id: 0,
            main: true,
            title: this.$t('Summary'),
          },
          ...this.tenderTemplate.map((list) => {
            return {
              ...list,
              count: this.selectedCounts[list.id].filter((id) => !this.mainRows.includes(id))
                .length,
            };
          }),
        ];
      },
    },
    methods: {
      ...mapMutations('tenderEdit', ['changeTender']),
      ...mapActions({
        confirm: 'confirmModal/openModal',
        closeModal: 'modal/closeModal',
      }),
      selectTab(id) {
        this.currentId = id;
      },
      positionsCount(itemId) {
        const itemsWithParentLevel = itemId
          ? this.selectedCounts[itemId]
          : Object.values(this.selectedCounts).flat();
        return itemsWithParentLevel?.filter((id) => !this.mainRows.includes(id))?.length || 0;
      },
      handleSelection(id, rowIds, isAll = false) {
        add(this.selectedSummary, id);

        this.selectedCounts = Object.assign({}, this.selectedCounts, {
          [id]: rowIds,
        });

        if (isAll) {
          if (!rowIds.length) {
            this.selectedSummary = this.selectedSummary.filter((summary) => summary !== id);
          }
        }
      },
      async updateRows({ addList, id }) {
        if (addList) {
          this.selectedSummary.push(id);

          // При выборе сметы, в которой ничего не выбрано, выбираем всё!
          if (this.selectedCounts[id].length === 0) {
            this.selectAllRows(id);
          }
        } else {
          this.selectedSummary = this.selectedSummary.filter((listId) => listId !== id);
          this.deselectAllRows(id);
        }
      },
      selectAllRows(listId) {
        this.selectedCounts = {
          ...this.selectedCounts,
          [listId]: this.tenderTemplate
            .find((list) => list.id === listId)
            ?.rows.map((row) => row.id)
            .filter((row) => !this.disabledRows.includes(row)),
        };
      },
      deselectAllRows(listId) {
        this.selectedCounts = {
          ...this.selectedCounts,
          [listId]: [],
        };
      },
      nextStep() {
        const originalSelectedRowsSet = new Set(
          Object.values(this.originalSelectedCounts).reduce((prev, cur) => [...prev, ...cur], [])
        );
        const selectedRowsSet = new Set(
          Object.values(this.selectedCounts).reduce((prev, cur) => [...prev, ...cur], [])
        );
        const isRowsChanged = !areSetsEqual(originalSelectedRowsSet, selectedRowsSet);
        if (this.editId && isRowsChanged) {
          this.confirm({
            title: this.$t('You changed several items in the tender'),
            actionText: this.$t('Save tender'),
            cancelText: this.$t('Return to back'),
            description: this.$t(
              "All already submitted offers from bidders will be marked as old. This will help the bidder send you the new offer, and you won't get confused when comparing them."
            ),
            action: () => {
              this.goToSecondStep();
            },
          });
        } else this.goToSecondStep();
      },
      goToSecondStep() {
        let rows = [];
        this.selectedSummary.forEach((summaryId) => {
          rows.push(this.selectedCounts[summaryId]);
        });
        this.changeTender({
          key: 'row_ids',
          value: mergeArrays(rows),
        });
        this.$emit('change-step', 2);
      },
      async updateDisabledRows(selectedRows) {
        let { data } = await request.get(`/tenders/rows_in_tenders/`, {
          params: {
            project_id: this.$route.params.id,
          },
        });

        this.disabledRows = data.filter((id) => !selectedRows.includes(id));
      },
    },
  };
</script>
